import { z } from "zod";
import {
  addressInitialValues,
  addressValidation,
} from "components/Composite/AddressField/helpers";
import { User } from "lib/auth";
import { Basket, DeliveryOption } from "lib/types";
import { validPhoneNumber } from "lib/schemas";

export interface InitialDetails {
  contact: Exclude<Basket["contact"], null | undefined>;
}

export const getValidationSchema = (
  hasDeliveryOptions: boolean,
  hasProhibitedItems: boolean,
  rangeDeliveryOptions: DeliveryOption | undefined,
  surcharges: string[] | undefined,
  categorySku: string,
  collectionDateEnabled: boolean,
) => {
  return z.object({
    deliveryAddress: z.object({
      ...addressValidation,
      lat: z.number().nullish(),
      lng: z.number().nullish(),
    }),
    saveAddress: z.boolean(),
    saveAddressToggle: z.boolean(),
    extras: z.object(
      (surcharges ?? []).reduce<Record<string, z.ZodNumber>>(
        (surcharges, surcharge) => {
          return {
            ...surcharges,
            [surcharge]: z.number().min(0).max(99),
          };
        },
        {},
      ),
    ),

    contact: z.object({
      firstName: z
        .string()
        .min(1)
        .regex(/^[^\d]+$/, "Letters only"),
      lastName: z
        .string()
        .min(1)
        .regex(/^[^\d]+$/, "Letters only"),
      email: z.string().min(1).email(),
      phoneNumber: validPhoneNumber,
    }),

    date: z.string().min(1),
    collectionDate: collectionDateEnabled
      ? z.string().min(1)
      : z.string().optional(),
    deliveryCharge: hasDeliveryOptions ? z.number() : z.number().optional(),

    /**
     * Having to group in an object as global refines get short circuited if
     * any inner object errors are thrown: https://github.com/colinhacks/zod/issues/479
     */
    delivery: z
      .object({
        deliveryOption: hasDeliveryOptions
          ? z.string().min(1)
          : z.string().optional(),
        timeslot: z.string().optional(),
      })
      .refine(
        ({ deliveryOption, timeslot }) =>
          !(
            rangeDeliveryOptions &&
            deliveryOption === rangeDeliveryOptions.title &&
            !timeslot
          ),
        {
          message: "common.form.errors.requiredField",
          path: ["timeslot"],
        },
      ),

    quantity: z.number().min(1).max(14),
    description: z.string().min(1),
    prohibitedItems: hasProhibitedItems
      ? z.literal(true)
      : z.boolean().optional(),
    privateLand:
      categorySku === "SKIPS" ? z.literal(true) : z.boolean().optional(),
  });
};

/**
 * Customer details initial details.
 */
export const getInitialDetails = (user: User | null): InitialDetails => ({
  contact: {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    phoneNumber: user?.phoneNumber ?? "",
  },
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialValues = (
  postcode: string,
  deliveryOption: string | undefined,
  hasProhibitedItems: boolean,
  surcharges: string[] | undefined,
  user: User | null,
  quantity: number,
) => {
  return {
    ...getInitialDetails(user),
    deliveryAddress: {
      ...addressInitialValues,
      postcode,
      lat: null,
      lng: null,
    },
    extras:
      surcharges?.reduce<Record<string, number>>((surcharges, surcharge) => {
        return {
          ...surcharges,
          [surcharge]: 0,
        };
      }, {}) ?? {},

    date: "",
    collectionDate: "",
    deliveryCharge: deliveryOption ? 0 : undefined,
    delivery: {
      deliveryOption,
      timeslot: deliveryOption ? "" : undefined,
    },
    quantity,
    description: "",
    prohibitedItems: hasProhibitedItems ? false : undefined,
    privateLand: false,
    saveAddress: false,
    saveAddressToggle: false,
  };
};
